/**
 * RSI(2) Oversold Pullback view (Investment Ideas — research and screening
 * model, NOT an Algo Strategy). Rendered as one of `MarketOpportunitiesPage`'s
 * flat-nav views (alongside Breakouts/Top Breakout/Market Cap Segments/etc.),
 * not a top-level Investing Idea tab of its own — see that file's
 * `FLAT_VIEW_TABS`/`viewMode === "rsi2-pullback"` branch. Read-only: this
 * page never triggers a calculation, never places or previews a broker
 * order, and never shows BUY/SELL/STRONG BUY/STRONG SELL/GUARANTEED
 * wording — only the approved terminology the backend itself returns
 * (`target_status_label`).
 *
 * Three parts, all sourced from `rsi2PullbackService`:
 *   1. The mandatory educational disclaimer banner (verbatim from the
 *      backend's `disclaimer` field on every response — never hard-coded
 *      here, so it always matches `app.services.rsi2_pullback.compliance`).
 *   2. A compact Historical Research Statistics summary strip.
 *   3. A paginated Investment Ideas table; clicking a row opens a detail
 *      dialog (fetched on demand, same "only when expanded" convention as
 *      `StockResearchDialog`/`BreakoutBreakdown`) showing the full
 *      screening snapshot and Outcome tracking section.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import rsi2PullbackService from "@/services/rsi2PullbackService";
import type {
  Rsi2PullbackIdeaDetailResponse,
  Rsi2PullbackIdeaRow,
  Rsi2PullbackSortField,
  Rsi2PullbackStatisticsResponse,
  SortOrder,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import Rsi2ChartPanel from "./rsi2-pullback/Rsi2ChartPanel";
import { formatDate, num, rsi2ChartStandaloneUrl, StatusBadge, tradingDaysLabel } from "./rsi2-pullback/rsi2ChartHelpers";

// Persists the user's chosen sort column/direction across page refreshes,
// same convention as `PortfolioTrackerPage.tsx`'s `SORT_STORAGE_KEY` -- a
// distinct key here so the two pages' persisted sort state never collide.
const SORT_STORAGE_KEY = "tekinvest.rsi2Pullback.sort";

interface StoredSort {
  field: Rsi2PullbackSortField;
  order: SortOrder;
}

function loadStoredSort(): StoredSort | null {
  try {
    const raw = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSort;
    if (!parsed.field || (parsed.order !== "asc" && parsed.order !== "desc")) return null;
    return parsed;
  } catch {
    return null;
  }
}

interface SortableColumn {
  field: Rsi2PullbackSortField;
  label: string;
  align?: "left" | "right";
}

// Exactly the 5 columns the spec calls out as sortable -- Company, RSI(2),
// Decline Days, Reference Price, Candle Chart stay plain (non-sortable)
// `<TableCell>`s.
const SORTABLE_COLUMNS: SortableColumn[] = [
  { field: "symbol", label: "Symbol" },
  { field: "sector", label: "Sector" },
  { field: "signal_date", label: "Signal Date" },
  { field: "indicative_target_price", label: "Target %", align: "right" },
  { field: "target_status", label: "Status" },
];

function StatisticsStrip({ stats }: { stats: Rsi2PullbackStatisticsResponse | null }) {
  if (!stats) return null;
  const items: { label: string; value: string }[] = [
    { label: "Total Setups", value: String(stats.total_signals) },
    { label: "Resolved", value: String(stats.resolved_signals) },
    { label: "Still Open", value: String(stats.still_pending) },
    {
      label: "Hit Rate",
      value: stats.hit_rate_pct !== null && stats.hit_rate_pct !== undefined ? `${num(stats.hit_rate_pct)}%` : "—",
    },
    {
      label: "Avg Return to Target",
      value:
        stats.avg_return_to_target_pct !== null && stats.avg_return_to_target_pct !== undefined
          ? `${num(stats.avg_return_to_target_pct)}%`
          : "—",
    },
    {
      label: "Avg Days to Target",
      value:
        stats.avg_trading_days_to_target !== null && stats.avg_trading_days_to_target !== undefined
          ? num(stats.avg_trading_days_to_target, 1)
          : "—",
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Stack direction="row" flexWrap="wrap" columnGap={3} rowGap={1} alignItems="baseline">
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0F172A" }}>
          Historical Research Statistics
        </Typography>
        {items.map((item) => (
          <Box key={item.label} sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}>
            <Typography component="span" variant="body2" color="text.secondary">
              {item.label}:
            </Typography>
            <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Stack>
      {stats.date_range_start && stats.date_range_end && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Based on {stats.total_signals} setups from {formatDate(stats.date_range_start)} to{" "}
          {formatDate(stats.date_range_end)}. Past performance does not guarantee future results.
        </Typography>
      )}
    </Paper>
  );
}

function IdeaDetailDialog({
  symbol,
  signalDate,
  onClose,
}: {
  symbol: string;
  signalDate: string;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<Rsi2PullbackIdeaDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    rsi2PullbackService
      .getDetail(symbol, signalDate)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) setError(extractErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [symbol, signalDate]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {symbol} — {formatDate(signalDate)}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {detail && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Why this idea?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {detail.why_this_idea}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Screening Snapshot
              </Typography>
              <Stack direction="row" flexWrap="wrap" columnGap={3} rowGap={0.5}>
                <Typography variant="body2">Close: ₹{num(detail.close_at_signal)}</Typography>
                <Typography variant="body2">Signal-Day Low: ₹{num(detail.signal_day_low)}</Typography>
                <Typography variant="body2">RSI(2): {num(detail.rsi2_at_signal)}</Typography>
                <Typography variant="body2">SMA(50): {num(detail.sma_50_at_signal)}</Typography>
                <Typography variant="body2">SMA(200): {num(detail.sma_200_at_signal)}</Typography>
                <Typography variant="body2">Decline Days: {detail.consecutive_decline_days}</Typography>
                <Typography variant="body2">Avg Turnover: ₹{num(detail.avg_turnover_20d_cr)} Cr</Typography>
              </Stack>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Indicative Research Target
              </Typography>
              <Stack direction="row" flexWrap="wrap" columnGap={3} rowGap={0.5}>
                <Typography variant="body2">Reference Price: ₹{num(detail.reference_price)}</Typography>
                <Typography variant="body2">Target: ₹{num(detail.indicative_target_price)}</Typography>
                <Typography variant="body2">Target %: {num(detail.target_pct)}%</Typography>
                <Typography variant="body2">Risk Level: ₹{num(detail.indicative_risk_level)}</Typography>
                <Typography variant="body2">Observation Days: {detail.observation_days}</Typography>
              </Stack>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Outcome
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <StatusBadge status={detail.target_status} label={detail.target_status_label} />
                {detail.outcome.same_day_ambiguous && (
                  <Tooltip title="Target and risk level were both first reached on the same trading day — the true intraday order is unknown from daily OHLC data alone.">
                    <Chip
                      icon={<InfoOutlinedIcon fontSize="small" />}
                      label="Same-day ambiguous"
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                )}
              </Stack>
              <Stack direction="row" flexWrap="wrap" columnGap={3} rowGap={0.5}>
                <Typography variant="body2">Target Hit Date: {formatDate(detail.outcome.target_hit_date)}</Typography>
                <Typography variant="body2">Target Hit Price: ₹{num(detail.outcome.target_hit_price)}</Typography>
                <Typography variant="body2">
                  Trading Days to Target: {tradingDaysLabel(detail.outcome.trading_days_to_target)}
                </Typography>
                <Typography variant="body2">Max Price Reached: ₹{num(detail.outcome.max_price_reached)}</Typography>
                <Typography variant="body2">
                  Observation Period Close: ₹{num(detail.outcome.observation_period_close)}
                </Typography>
              </Stack>
            </Box>
            <Divider />
            <Alert severity="info" variant="outlined">
              {detail.disclaimer}
            </Alert>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChartDialog({
  row,
  onClose,
}: {
  row: Rsi2PullbackIdeaRow;
  onClose: () => void;
}) {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {row.symbol} — RSI(2) Oversold Pullback
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Rsi2ChartPanel
          symbol={row.symbol}
          signalDate={row.signal_date}
          initialRow={row}
          onOpenInNewTab={() =>
            window.open(rsi2ChartStandaloneUrl(row.symbol, row.signal_date), "_blank", "noopener,noreferrer")
          }
        />
      </DialogContent>
    </Dialog>
  );
}

export function Rsi2PullbackPage() {
  const [rows, setRows] = useState<Rsi2PullbackIdeaRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disclaimer, setDisclaimer] = useState<string | null>(null);
  const [stats, setStats] = useState<Rsi2PullbackStatisticsResponse | null>(null);
  const [selected, setSelected] = useState<{ symbol: string; signalDate: string } | null>(null);
  const [chartSelected, setChartSelected] = useState<Rsi2PullbackIdeaRow | null>(null);

  // `null` means "no column explicitly chosen yet" -- the table then shows
  // the backend's own default (newest signal_date first) and omits
  // sort_by/sort_order from the API call entirely. Restored from
  // localStorage so a previously-chosen sort survives a page refresh, same
  // convention as `PortfolioTrackerPage.tsx`.
  const [sortBy, setSortBy] = useState<Rsi2PullbackSortField | null>(() => loadStoredSort()?.field ?? null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => loadStoredSort()?.order ?? "desc");

  // Persist the active sort so it's still applied after a page refresh.
  useEffect(() => {
    if (!sortBy) return;
    try {
      window.localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ field: sortBy, order: sortOrder }));
    } catch {
      // localStorage can throw in private-browsing/storage-full edge cases --
      // sorting still works for the current session, it just won't persist.
    }
  }, [sortBy, sortOrder]);

  const loadIdeas = useCallback(() => {
    setLoading(true);
    setError(null);
    rsi2PullbackService
      .list({
        page: page + 1,
        page_size: pageSize,
        sort_by: sortBy ?? undefined,
        sort_order: sortBy ? sortOrder : undefined,
      })
      .then((response) => {
        setRows(response.items);
        setTotal(response.meta.total);
        setDisclaimer(response.disclaimer);
      })
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    loadIdeas();
  }, [loadIdeas]);

  // Clicking a column header the first time sorts ascending; clicking the
  // SAME column again toggles to descending, then back to ascending.
  // Clicking a DIFFERENT column switches to it, defaulting to ascending.
  // Re-sorting always jumps back to page 1.
  const handleSort = (field: Rsi2PullbackSortField) => {
    setSortOrder((prevOrder) => (sortBy === field ? (prevOrder === "asc" ? "desc" : "asc") : "asc"));
    setSortBy(field);
    setPage(0);
  };

  useEffect(() => {
    rsi2PullbackService
      .getStatistics()
      .then(setStats)
      .catch(() => {
        // Statistics strip is supplementary -- fail silently rather than
        // blocking the ideas table with a page-level error banner.
      });
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        RSI(2) Oversold Pullback
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A research and screening model — not a buy/sell recommendation. Identifies NSE stocks matching the
        RSI(2) Oversold Pullback setup and tracks price outcome against a mechanically calculated Indicative
        Research Target.
      </Typography>

      {disclaimer && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {disclaimer}
        </Alert>
      )}

      <StatisticsStrip stats={stats} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {SORTABLE_COLUMNS.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align ?? "left"}
                    sortDirection={sortBy === column.field ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === column.field}
                      direction={sortBy === column.field ? sortOrder : "asc"}
                      onClick={() => handleSort(column.field)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell>Company</TableCell>
                <TableCell align="right">RSI(2)</TableCell>
                <TableCell align="right">Decline Days</TableCell>
                <TableCell align="right">Reference Price</TableCell>
                <TableCell align="right">Current Price</TableCell>
                <TableCell align="center">Candle Chart</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No RSI(2) Oversold Pullback setups found yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => (
                  <TableRow
                    key={`${row.symbol}-${row.signal_date}`}
                    hover
                    onClick={() => setSelected({ symbol: row.symbol, signalDate: row.signal_date })}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{row.symbol}</TableCell>
                    <TableCell>{row.sector ?? "—"}</TableCell>
                    <TableCell>{formatDate(row.signal_date)}</TableCell>
                    <TableCell align="right">{row.target_pct ? `${num(row.target_pct)}%` : "—"}</TableCell>
                    <TableCell>
                      <StatusBadge status={row.target_status} label={row.target_status_label} />
                    </TableCell>
                    <TableCell>{row.company_name}</TableCell>
                    <TableCell align="right">{num(row.rsi2_at_signal)}</TableCell>
                    <TableCell align="right">{row.consecutive_decline_days}</TableCell>
                    <TableCell align="right">
                      {row.reference_price ? `₹${num(row.reference_price)}` : "Pending"}
                    </TableCell>
                    <TableCell align="right">
                      {row.current_price ? `₹${num(row.current_price)}` : "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Candle Chart">
                        <IconButton
                          size="small"
                          aria-label="View Candle Chart"
                          onClick={(e) => {
                            e.stopPropagation();
                            setChartSelected(row);
                          }}
                        >
                          <CandlestickChartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      </Paper>

      {disclaimer && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {disclaimer}
        </Alert>
      )}

      {selected && (
        <IdeaDetailDialog
          symbol={selected.symbol}
          signalDate={selected.signalDate}
          onClose={() => setSelected(null)}
        />
      )}

      {chartSelected && <ChartDialog row={chartSelected} onClose={() => setChartSelected(null)} />}
    </Box>
  );
}

export default Rsi2PullbackPage;
